import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router";
import { useTranslation } from "react-i18next";
import { cn } from "~/lib/cn";
import type { NavSessionUser } from "~/lib/nav-session";
import { resolveRouteFromPathname } from "~/lib/locale-path";
import { Button } from "~/components/ui";
import { AppLink } from "~/components/ui/AppLink";
import { LanguageSwitcher } from "~/components/ui/LanguageSwitcher";
import { IconClose } from "~/components/layout/nav-icons";
import { PUBLIC_NAV } from "~/components/layout/AppHeader";
import { Wordmark } from "~/components/layout/Wordmark";
import { useSignOut } from "~/components/layout/use-sign-out";

interface CuterProps {
  isOpen: boolean;
  onClose: () => void;
  /** T872 S1 — a member on a public route gets the account group instead of «Iniciar sesión». */
  sessionUser?: NavSessionUser | null;
}

/** Trigger for the sticky-bar choreography (states/cuter.jsx): scrollTop > 8px. */
const CUTER_SCROLL_THRESHOLD = 8;

/**
 * «Cúter» — full-screen modal navigation for the public header (<48em — 768px at the default text size, T895), reshaped by
 * T872 S1 to the October delivery: cream, logo and close on top, the four public
 * destinations in serif with the current page in core ink, then a bottom group
 * (guest: auto-test primary + sign-in; member: Mi espacio primary, Mi perfil, sign
 * out) and the `ES / EN` row. Dialog semantics, focus trap, Escape and body scroll
 * lock are T98/T226's and unchanged; the T226 scroll choreography stays armed for
 * landscape and 200% text.
 */
export function Cuter({ isOpen, onClose, sessionUser }: CuterProps) {
  const { t } = useTranslation("nav");
  const { pathname } = useLocation();
  const current = resolveRouteFromPathname(pathname)?.routeKey;
  const dialogRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  const [scrolledPast, setScrolledPast] = useState(false);
  const [atEnd, setAtEnd] = useState(true);
  const { signOut, signingOut, signOutError } = useSignOut(onClose);

  const measureScroll = useCallback(() => {
    const el = bodyRef.current;
    if (!el) return;
    setScrolledPast(el.scrollTop > CUTER_SCROLL_THRESHOLD);
    setAtEnd(el.scrollTop + el.clientHeight >= el.scrollHeight - 1);
  }, []);

  // The cúter is the phone's navigation: a rotation or resize past 48em closes it, so
  // its scroll lock, scrim and focus trap never outlive the CSS that hides it
  // (Codex's preview review, 2026-10-10: open at 390, turned to 844 — a blurred,
  // locked page with no close control).
  useEffect(() => {
    if (!isOpen || typeof window.matchMedia !== "function") return;
    const desktop = window.matchMedia("(min-width: 48em)");
    const closeOnDesktop = () => {
      if (desktop.matches) onClose();
    };
    closeOnDesktop();
    desktop.addEventListener("change", closeOnDesktop);
    return () => desktop.removeEventListener("change", closeOnDesktop);
  }, [isOpen, onClose]);

  // Initial measure on open — decides whether the bottom fade shows at rest.
  useEffect(() => {
    if (isOpen) measureScroll();
  }, [isOpen, measureScroll]);

  useEffect(() => {
    if (!isOpen) return;
    // The control that opened the cúter gets focus back when it closes (Escape, close
    // button or scrim); after a navigation the router owns focus and the opener is gone.
    const opener = document.activeElement as HTMLElement | null;
    closeRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
        return;
      }
      // Focus trap: keep Tab / Shift+Tab cycling inside the dialog.
      if (e.key !== "Tab") return;
      const root = dialogRef.current;
      if (!root) return;
      const focusables = root.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [contenteditable], [tabindex]:not([tabindex="-1"])',
      );
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
      if (opener && document.contains(opener) && typeof opener.focus === "function") opener.focus();
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div
      ref={dialogRef}
      className="fixed inset-0 z-[101] md:hidden"
      role="dialog"
      aria-modal="true"
      aria-label={t("primary_label")}
    >
      <div className="cuter cuter--public">
        <div className={cn("cuter-bar", scrolledPast && "is-scrolled")}>
          {/* The logo always means Home (navigation handoff); a member takes the public
              home, as from the bar (T634 `?public=1`). */}
          <Wordmark
            routeKey="home"
            search={sessionUser ? "?public=1" : undefined}
            aria-label={t("home_aria")}
            onClick={onClose}
          />
          <Button
            ref={closeRef}
            type="button"
            variant="icon"
            className="bare"
            aria-label={t("menu_close")}
            onClick={onClose}
          >
            <IconClose />
          </Button>
        </div>

        <div className="cuter-scroll-wrap">
          {scrolledPast ? <div className="cuter-fade-top" aria-hidden="true" /> : null}
          <div className="cuter-body" ref={bodyRef} onScroll={measureScroll}>
            <nav className="cuter-nav" aria-label={t("primary_label")}>
              {PUBLIC_NAV.map(({ routeKey, labelKey }) => (
                <AppLink
                  key={routeKey}
                  routeKey={routeKey}
                  aria-current={current === routeKey ? "page" : undefined}
                  onClick={onClose}
                >
                  {t(labelKey)}
                </AppLink>
              ))}
            </nav>

            <div className="cuter-actions">
              {sessionUser ? (
                <>
                  <AppLink routeKey="dashboard" variant="public-primary" className="w-full" onClick={onClose}>
                    {t("links.dashboard")}
                  </AppLink>
                  <AppLink routeKey="profile" className="cuter-link" onClick={onClose}>
                    {t("public.my_profile")}
                  </AppLink>
                  {signOutError ? (
                    <p className="cuter-error" role="alert">
                      {t("user_menu.signout_error_title")}
                    </p>
                  ) : null}
                  <Button
                    type="button"
                    variant="unstyled"
                    className="cuter-link"
                    onClick={signOut}
                    disabled={signingOut}
                    isLoading={signingOut}
                    busyLabel={t("user_menu.signing_out")}
                  >
                    {signOutError
                        ? t("user_menu.error_retry")
                        : t("user_menu.log_out")}
                  </Button>
                </>
              ) : (
                <>
                  <AppLink routeKey="autoTest" variant="public-primary" className="w-full" onClick={onClose}>
                    {t("public.self_test_cta")}
                  </AppLink>
                  <AppLink routeKey="signup" variant="public-secondary" className="w-full" onClick={onClose}>
                    {t("sign_in")}
                  </AppLink>
                </>
              )}
            </div>

            <div className="cuter-foot">
              <LanguageSwitcher variant="text" />
            </div>
          </div>
          {!atEnd ? <div className="cuter-fade-bot" aria-hidden="true" /> : null}
        </div>
      </div>
    </div>
  );
}
